/**
 * MODULES_CONFIG - Single Source of Truth
 *
 * Este archivo centraliza la configuración de módulos para:
 * - Sidebar navigation
 * - Permissions page (RolesPermissionsPageV2)
 * - Backend sync (RolePermission.MODULES)
 *
 * IMPORTANTE: Cualquier cambio aquí debe reflejarse en el backend:
 * backend/apps/users/models.py -> RolePermission.MODULES
 */

// Configuration types - no React imports needed here

// ============================================================================
// TYPES
// ============================================================================

/**
 * Item group within a module - for organizing submodules
 */
export interface ItemGroup {
  id: string;
  label: string;
  labelEn?: string;
  icon?: string;
  description?: string;
}

/**
 * Scope type for modules and items
 * - 'shared': Visible for both Platform (OWLY) and Tenant users
 * - 'platform': Only visible for Platform (OWLY) users
 * - 'tenant': Only visible for Tenant (client company) users
 */
export type ModuleScope = 'shared' | 'platform' | 'tenant';

/**
 * Individual navigation item / submodule
 */
export interface ModuleItem {
  id: string;
  label: string;
  globalLabel?: string;    // Label when viewing all (OWLY mode)
  projectLabel?: string;   // Label when project is selected
  icon: string;            // Icon key (mapped in sidebar)
  href: string;
  badge?: string | number;
  badgeVariant?: 'default' | 'primary' | 'success' | 'warning' | 'info' | 'danger';
  requiredPermission?: string; // e.g., 'leads.view'
  matchPaths?: string[];   // Additional paths that should highlight this item (supports wildcards like '/pipelines/*')
  group?: string;          // Group ID for categorization within module

  // === UNIFIED ARCHITECTURE ===
  scope?: ModuleScope;     // Overrides module scope for this item (default: inherits from module)
  platformTag?: 'PLATFORM'; // Shows "PLATFORM" tag in sidebar for platform-only items
}

/**
 * Module/Section configuration
 */
export interface ModuleConfig {
  id: string;              // Must match backend RolePermission.MODULES
  label: string;
  labelEn?: string;
  icon: string;            // Icon key
  scope: ModuleScope;      // 'shared' | 'platform' | 'tenant'
  owlyOnly?: boolean;      // @deprecated - use scope: 'platform' instead
  platformTag?: 'PLATFORM'; // Shows "PLATFORM" tag in sidebar for platform-only modules
  requiredPermission?: string; // Section-level permission
  groups?: ItemGroup[];    // Groups for organizing items within this module
  items: ModuleItem[];
}

// ============================================================================
// STANDARD ITEM GROUPS - Reusable across modules
// ============================================================================

export const STANDARD_GROUPS = {
  // Common groups used across multiple modules
  main: { id: 'main', label: 'Principal', labelEn: 'Main', icon: 'HomeIcon' },
  operations: { id: 'operations', label: 'Operaciones', labelEn: 'Operations', icon: 'CogIcon' },
  analytics: { id: 'analytics', label: 'Análisis', labelEn: 'Analytics', icon: 'ChartBarIcon' },
  config: { id: 'config', label: 'Configuración', labelEn: 'Configuration', icon: 'Cog6ToothIcon' },

  // Domain-specific groups
  structure: { id: 'structure', label: 'Estructura', labelEn: 'Structure', icon: 'BuildingOffice2Icon' },
  inventory: { id: 'inventory', label: 'Inventario', labelEn: 'Inventory', icon: 'CubeIcon' },
  media: { id: 'media', label: 'Contenido', labelEn: 'Content', icon: 'PhotoIcon' },
  pipeline: { id: 'pipeline', label: 'Pipeline', labelEn: 'Pipeline', icon: 'FunnelIcon' },
  lifecycle: { id: 'lifecycle', label: 'Ciclo de Vida', labelEn: 'Lifecycle', icon: 'ArrowPathIcon' },
  financial: { id: 'financial', label: 'Financiero', labelEn: 'Financial', icon: 'BanknotesIcon' },
  channels: { id: 'channels', label: 'Canales', labelEn: 'Channels', icon: 'ChatBubbleLeftIcon' },
  ai: { id: 'ai', label: 'Inteligencia Artificial', labelEn: 'AI', icon: 'SparklesIcon' },
  users: { id: 'users', label: 'Usuarios', labelEn: 'Users', icon: 'UsersIcon' },
  performance: { id: 'performance', label: 'Rendimiento', labelEn: 'Performance', icon: 'ChartBarSquareIcon' },
  org: { id: 'org', label: 'Organización', labelEn: 'Organization', icon: 'BuildingOffice2Icon' },
  infrastructure: { id: 'infrastructure', label: 'Infraestructura', labelEn: 'Infrastructure', icon: 'ServerIcon' },
  docs: { id: 'docs', label: 'Documentación', labelEn: 'Documentation', icon: 'BookOpenIcon' },
  testing: { id: 'testing', label: 'Testing', labelEn: 'Testing', icon: 'BeakerIcon' },
} as const;

// ============================================================================
// PERMISSION ACTIONS - Same as backend
// ============================================================================

export const PERMISSION_ACTIONS = [
  { code: 'view', label: 'Ver', labelEn: 'View' },
  { code: 'create', label: 'Crear', labelEn: 'Create' },
  { code: 'update', label: 'Editar', labelEn: 'Update' },
  { code: 'delete', label: 'Eliminar', labelEn: 'Delete' },
  { code: 'assign', label: 'Asignar', labelEn: 'Assign' },
  { code: 'approve', label: 'Aprobar', labelEn: 'Approve' },
  { code: 'export', label: 'Exportar', labelEn: 'Export' },
] as const;

export type PermissionAction = typeof PERMISSION_ACTIONS[number]['code'];

// ============================================================================
// MODULES CONFIGURATION
// ============================================================================

export const MODULES_CONFIG: ModuleConfig[] = [
  // ========== PRINCIPAL ==========
  // CONSISTENT: All items use 'dashboard.view' as base permission
  // Submodule visibility controlled by 'dashboard.{itemId}'
  {
    id: 'dashboard',
    label: 'Principal',
    labelEn: 'Main',
    icon: 'HomeIcon',
    scope: 'shared',
    requiredPermission: 'dashboard.view',
    groups: [
      { id: 'dashboards', label: 'Dashboards por Rol', labelEn: 'Role Dashboards', icon: 'HomeIcon' },
      { id: 'analytics', label: 'Análisis', labelEn: 'Analytics', icon: 'ChartBarIcon' },
    ],
    items: [
      {
        id: 'main',
        label: 'Dashboard',
        globalLabel: 'Dashboard Global',
        projectLabel: 'Dashboard Proyecto',
        icon: 'HomeIcon',
        href: '/dashboard',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },     
      {
        id: 'agent',
        label: 'Dashboard Agente',
        icon: 'UserGroupIcon',
        href: '/dashboard/agent',
        badge: 'Agente',
        badgeVariant: 'success',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'team',
        label: 'Dashboard Equipo',
        icon: 'UsersIcon',
        href: '/dashboard/team',
        badge: 'Manager',
        badgeVariant: 'info',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'executive',
        label: 'Dashboard Ejecutivo',
        icon: 'ChartBarSquareIcon',
        href: '/dashboard/executive',
        badge: 'Ejecutivo',
        badgeVariant: 'warning',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'president',
        label: 'Dashboard Presidente',
        icon: 'SparklesIcon',
        href: '/dashboard/president',
        badge: 'CEO',
        badgeVariant: 'primary',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
       {
        id: 'panel-seguimiento',
        label: 'Panel de seguimiento',
        icon: 'ClipboardDocumentListIcon',
        href: '/dashboard/panel-seguimiento',
        badge: 'NEW',
        badgeVariant: 'danger',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'advisor-v2',
        label: 'Dashboard Asesor 2.0',
        icon: 'RocketLaunchIcon',
        href: '/dashboard/advisor-v2',
        badge: 'NEW',
        badgeVariant: 'success',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'advisor',
        label: 'Dashboard Asesor',
        icon: 'UserCircleIcon',
        href: '/dashboard/advisor',
        badge: 'Full',
        badgeVariant: 'primary',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'clients-dashboard',
        label: 'Dashboard Clientes',
        icon: 'UserGroupIcon',
        href: '/dashboard/clients',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'owly-bi',
        label: 'Owly BI',
        icon: 'Squares2X2Icon',
        href: '/dashboard/owly-bi',
        badge: 'NEW',
        badgeVariant: 'success',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'business-value',
        label: 'Valor de Negocio',
        icon: 'TrophyIcon',
        href: '/dashboard/business-value',
        badge: 'OWLY',
        badgeVariant: 'success',
        requiredPermission: 'dashboard.view',
        group: 'analytics',
      },
      {
        id: 'marketing-dashboard',
        label: 'Dashboard Marketing',
        icon: 'MegaphoneIcon',
        href: '/dashboard/marketing',
        badge: 'NEW',
        badgeVariant: 'success',
        requiredPermission: 'dashboard.view',
        group: 'analytics',
      },
      // OwlySales v2 Dashboards
      {
        id: 'owlysales-comando',
        label: 'Comando Central',
        icon: 'BoltIcon',
        href: '/dashboard/comando-central',
        badge: 'v2',
        badgeVariant: 'primary',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'owlysales-ventas',
        label: 'Mis Ventas',
        icon: 'ArrowTrendingUpIcon',
        href: '/dashboard/mis-ventas',
        badge: 'v2',
        badgeVariant: 'success',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'owlysales-adquisicion',
        label: 'Adquisición',
        icon: 'MegaphoneIcon',
        href: '/dashboard/adquisicion',
        badge: 'v2',
        badgeVariant: 'info',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'owlysales-inventario',
        label: 'Inventario',
        icon: 'BuildingOffice2Icon',
        href: '/dashboard/inventario',
        badge: 'v2',
        badgeVariant: 'warning',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'owlysales-equipo',
        label: 'Equipo & Ops',
        icon: 'UsersIcon',
        href: '/dashboard/equipo-ops',
        badge: 'v2',
        badgeVariant: 'default',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      {
        id: 'broker',
        label: 'Portal Broker',
        icon: 'BuildingOffice2Icon',
        href: '/dashboard/broker',
        // Narrow match — subroutes moved to Learning must NOT activate this item.
        matchPaths: ['/dashboard/broker', '/dashboard/broker/register'],
        badge: 'Broker',
        badgeVariant: 'warning',
        requiredPermission: 'dashboard.view',
        group: 'dashboards',
      },
      // Broker uses standard items in their natural sections (leads, gestion, commissions)
      // controlled by permission_defaults.py — no duplicate broker-* items needed here.
      // Broker-personal training items (mi capacitación, mis certificaciones, examen,
      // compliance, acuerdo) live in the Learning module.
    ],
  },

  // ========== GESTIÓN ==========
  // CONSISTENT: All items use 'gestion.view' as base permission
  // Submodule visibility controlled by 'gestion.{itemId}'
  {
    id: 'gestion',
    label: 'Gestión',
    labelEn: 'Management',
    icon: 'Squares2X2Icon',
    scope: 'shared',
    requiredPermission: 'gestion.view',
    groups: [
      { id: 'structure', label: 'Estructura Organizacional', labelEn: 'Org Structure', icon: 'BuildingOffice2Icon' },
      { id: 'inventory', label: 'Inventario', labelEn: 'Inventory', icon: 'CubeIcon' },
      { id: 'media', label: 'Contenido', labelEn: 'Content', icon: 'PhotoIcon' },
      { id: 'sales', label: 'Ventas', labelEn: 'Sales', icon: 'DocumentTextIcon' },
      { id: 'analytics', label: 'Análisis Comercial', labelEn: 'Sales Analytics', icon: 'ChartBarIcon' },
    ],
    items: [
      {
        id: 'global-config',
        label: 'Datos Globales',
        globalLabel: 'Datos Globales',
        icon: 'Cog6ToothIcon',
        href: '/global-config',
        matchPaths: ['/global-config/*'],
        requiredPermission: 'gestion.view',
        group: 'structure',
      },
      {
        id: 'markets',
        label: 'Mercados',
        globalLabel: 'Todos los Mercados',
        icon: 'GlobeAmericasIcon',
        href: '/markets',
        matchPaths: ['/markets/*'],
        requiredPermission: 'gestion.view',
        group: 'structure',
      },
      {
        id: 'companies',
        label: 'Compañías',
        globalLabel: 'Todas las Compañías',
        icon: 'BuildingOffice2Icon',
        href: '/companies',
        matchPaths: ['/companies/*'],
        requiredPermission: 'gestion.view',
        group: 'structure',
      },
      {
        id: 'projects',
        label: 'Proyectos',
        globalLabel: 'Todos los Proyectos',
        projectLabel: 'Proyectos',
        icon: 'BuildingStorefrontIcon',
        href: '/projects',
        matchPaths: ['/projects/*'],
        requiredPermission: 'gestion.view',
        group: 'structure',
      },
      {
        id: 'assignable-areas',
        label: 'Áreas Asignables',
        globalLabel: 'Áreas Asignables',
        projectLabel: 'Áreas Asignables',
        icon: 'RectangleStackIcon',
        href: '/assignable-areas',
        requiredPermission: 'gestion.view',
        group: 'structure',
      },
      {
        id: 'availability',
        label: 'Disponibilidad',
        globalLabel: 'Disponibilidad',
        projectLabel: 'Disponibilidad',
        icon: 'CubeIcon',
        href: '/availability',
        // Mantener marcado "Disponibilidad" en el detalle de una unidad
        // (/availability/unit/:unitId), no que salte a Dashboard.
        matchPaths: ['/availability/*'],
        requiredPermission: 'gestion.view',
        group: 'inventory',
      },
      {
        id: 'units',
        label: 'Unidades',
        projectLabel: 'Unidades',
        icon: 'HomeModernIcon',
        href: '/units',
        requiredPermission: 'gestion.view',
        group: 'inventory',
      },
      {
        id: 'complementary-units',
        label: 'U. Complementarias',
        globalLabel: 'Unidades Complementarias',
        projectLabel: 'Unidades Complementarias',
        icon: 'Square3Stack3DIcon',
        href: '/complementary-units',
        requiredPermission: 'gestion.view',
        group: 'inventory',
      },
      {
        id: 'amenities',
        label: 'Amenidades',
        globalLabel: 'Amenidades',
        projectLabel: 'Amenidades',
        icon: 'SparklesIcon',
        href: '/amenities',
        requiredPermission: 'gestion.view',
        group: 'inventory',
      },
      {
        id: 'typologies',
        label: 'Tipologías',
        globalLabel: 'Tipologías',
        projectLabel: 'Tipologías',
        icon: 'Squares2X2Icon',
        href: '/typologies',
        requiredPermission: 'gestion.view',
        group: 'inventory',
      },
      {
        id: 'pricing',
        label: 'Pricing & Descuentos',
        projectLabel: 'Pricing & Descuentos',
        icon: 'BanknotesIcon',
        href: '/pricing',
        requiredPermission: 'gestion.view',
        group: 'inventory',
      },
      {
        id: 'maqueta-virtual',
        label: 'Maqueta Virtual',
        projectLabel: 'Maqueta Virtual',
        icon: 'CubeTransparentIcon',
        href: '/orbit-view',
        requiredPermission: 'gestion.view',
        group: 'media',
      },
      {
        id: 'floor-plans',
        label: 'Floor Plans',
        projectLabel: 'Floor Plans',
        icon: 'MapIcon',
        href: '/floor-plans',
        requiredPermission: 'gestion.view',
        group: 'media',
      },
      {
        id: 'gallery',
        label: 'Galería',
        projectLabel: 'Galería del Proyecto',
        icon: 'PhotoIcon',
        href: '/gallery',
        requiredPermission: 'gestion.view',
        group: 'media',
      },
      {
        id: 'media',
        label: 'Media',
        globalLabel: 'Media',
        projectLabel: 'Media del Proyecto',
        icon: 'FilmIcon',
        href: '/media',
        requiredPermission: 'gestion.view',
        group: 'media',
      },
      {
        id: 'quotes',
        label: 'Cotizaciones',
        globalLabel: 'Todas las Cotizaciones',
        projectLabel: 'Cotizaciones del Proyecto',
        icon: 'DocumentTextIcon',
        href: '/quotes',
        matchPaths: ['/quotes/*'],
        requiredPermission: 'gestion.view',
        group: 'sales',
      },
      {
        id: 'pricing-structure',
        label: 'Estructura de Precios',
        globalLabel: 'Costos de Venta - General',
        projectLabel: 'Costos de Venta - Proyecto',
        icon: 'ChartPieIcon',
        href: '/pricing-structure',
        requiredPermission: 'gestion.view',
        group: 'analytics',
      },
      {
        id: 'sales-budget',
        label: 'Presupuesto Comercial',
        globalLabel: 'Presupuestador de Ventas',
        projectLabel: 'Presupuesto Comercial',
        icon: 'DocumentChartBarIcon',
        href: '/sales-budget',
        requiredPermission: 'gestion.view',
        group: 'analytics',
      },
      {
        id: 'buyer-persona',
        label: 'Mi Cliente Ideal',
        projectLabel: 'Mi Cliente Ideal',
        icon: 'UserGroupIcon',
        href: '/buyer-persona',
        requiredPermission: 'gestion.view',
        group: 'analytics',
      },
    ],
  },

  // ========== LEADS ==========
  // CONSISTENT: All items use 'leads.view' as base permission
  // Submodule visibility controlled by 'leads.{itemId}'
  {
    id: 'leads',
    label: 'Leads',
    labelEn: 'Leads',
    icon: 'FunnelIcon',
    scope: 'shared',
    requiredPermission: 'leads.view',
    groups: [
      { id: 'operations', label: 'Operaciones', labelEn: 'Operations', icon: 'UsersIcon' },
      { id: 'pipeline', label: 'Pipeline', labelEn: 'Pipeline', icon: 'FunnelIcon' },
      { id: 'config', label: 'Configuración', labelEn: 'Configuration', icon: 'Cog6ToothIcon' },
    ],
    items: [
      {
        id: 'list',
        label: 'Lista de Leads',
        icon: 'UsersIcon',
        href: '/leads',
        matchPaths: ['/leads/*'],
        requiredPermission: 'leads.view',
        group: 'operations',
      },
      {
        id: 'personas',
        label: 'Personas',
        icon: 'UsersIcon',
        href: '/personas',
        requiredPermission: 'leads.view',
        group: 'operations',
      },
      {
        id: 'funnel',
        label: 'Funnel de Leads',
        icon: 'FunnelIcon',
        href: '/lead-funnel',
        requiredPermission: 'leads.view',
        group: 'pipeline',
      },
      {
        id: 'playbook',
        label: 'Playbook de Leads',
        icon: 'BookOpenIcon',
        href: '/lead-playbook',
        requiredPermission: 'leads.view',
        group: 'pipeline',
      },
      {
        id: 'scoring',
        label: 'Lead Scoring',
        icon: 'ChartBarSquareIcon',
        href: '/lead-scoring',
        requiredPermission: 'leads.view',
        group: 'pipeline',
      },
      {
        id: 'exit-reasons',
        label: 'Cancelación de Leads',
        icon: 'XCircleIcon',
        href: '/exit-reasons',
        requiredPermission: 'leads.view',
        group: 'config',
      },
      {
        id: 'transfer-reasons',
        label: 'Razones de Transferencia',
        icon: 'ArrowPathIcon',
        href: '/transfer-reasons',
        requiredPermission: 'leads.view',
        group: 'config',
      },
      {
        id: 'lead-sources',
        label: 'Fuentes de Leads',
        icon: 'SignalIcon',
        href: '/lead-sources',
        requiredPermission: 'leads.view',
        group: 'config',
      },
    ],
  },

  // ========== CLIENTES ==========
  // CONSISTENT: All items use 'clients.view' as base permission
  // Submodule visibility controlled by 'clients.{itemId}'
  {
    id: 'clients',
    label: 'Clientes',
    labelEn: 'Clients',
    icon: 'UserCircleIcon',
    scope: 'shared',
    requiredPermission: 'clients.view',
    groups: [
      { id: 'operations', label: 'Operaciones', labelEn: 'Operations', icon: 'UsersIcon' },
      { id: 'lifecycle', label: 'Ciclo de Vida', labelEn: 'Lifecycle', icon: 'ArrowPathIcon' },
      { id: 'financial', label: 'Financiero', labelEn: 'Financial', icon: 'BanknotesIcon' },
    ],
    items: [
      {
        id: 'list',
        label: 'Lista de Clientes',
        icon: 'UsersIcon',
        href: '/clients',
        requiredPermission: 'clients.view',
        group: 'operations',
      },
      {
        id: 'funnel',
        label: 'Funnel de Clientes',
        icon: 'FunnelIcon',
        href: '/client-funnel',
        badge: 'NEW',
        badgeVariant: 'primary',
        requiredPermission: 'clients.view',
        group: 'operations',
      },
      {
        id: 'scoring',
        label: 'Client Scoring',
        icon: 'ChartBarSquareIcon',
        href: '/clients/scoring',
        requiredPermission: 'clients.view',
        group: 'operations',
      },
      {
        id: 'churn',
        label: 'Cancelación de Clientes',
        icon: 'XCircleIcon',
        href: '/client-churn',
        requiredPermission: 'clients.view',
        group: 'lifecycle',
      },
      {
        id: 'deliveries',
        label: 'Entregas',
        icon: 'WrenchScrewdriverIcon',
        href: '/deliveries',
        requiredPermission: 'clients.view',
        group: 'lifecycle',
      },
      {
        id: 'support',
        label: 'Soporte',
        icon: 'FaceSmileIcon',
        href: '/support',
        requiredPermission: 'clients.view',
        group: 'lifecycle',
      },
      {
        id: 'billing',
        label: 'Facturación',
        icon: 'CreditCardIcon',
        href: '/billing',
        requiredPermission: 'clients.view',
        group: 'financial',
      },
      {
        id: 'financial',
        label: 'Financiero',
        icon: 'BanknotesIcon',
        href: '/financial',
        requiredPermission: 'clients.view',
        group: 'financial',
      },
    ],
  },

  // ========== EQUIPO ==========
  // CONSISTENT: All items use 'team.view' as base permission
  // Submodule visibility controlled by 'team.{itemId}'
  {
    id: 'team',
    label: 'Equipo',
    labelEn: 'Team',
    icon: 'UserGroupIcon',
    scope: 'shared',
    requiredPermission: 'team.view',
    groups: [
      { id: 'users', label: 'Gestión de Usuarios', labelEn: 'User Management', icon: 'UsersIcon' },
      { id: 'performance', label: 'Rendimiento', labelEn: 'Performance', icon: 'ChartBarSquareIcon' },
      { id: 'commissions', label: 'Comisiones', labelEn: 'Commissions', icon: 'BanknotesIcon' },
      { id: 'org', label: 'Organización', labelEn: 'Organization', icon: 'BuildingOffice2Icon' },
    ],
    items: [
      {
        id: 'roles',
        label: 'Roles',
        icon: 'ShieldCheckIcon',
        href: '/roles',
        requiredPermission: 'team.view',
        group: 'users',
      },
      {
        id: 'users',
        label: 'Gestión de Usuarios',
        globalLabel: 'Todos los Usuarios',
        projectLabel: 'Usuarios del Proyecto',
        icon: 'UsersIcon',
        href: '/users',
        matchPaths: ['/users/*'],
        requiredPermission: 'team.view',
        group: 'users',
      },
      {
        id: 'permissions',
        label: 'Permisos',
        icon: 'KeyIcon',
        href: '/permissions',
        requiredPermission: 'team.view',
        group: 'users',
      },
      {
        id: 'advisors',
        label: 'Gestión de Equipo',
        globalLabel: 'Gestión de Equipo',
        projectLabel: 'Equipo del Proyecto',
        icon: 'UsersIcon',
        href: '/team',
        requiredPermission: 'team.view',
        group: 'users',
      },
      {
        id: 'dashboard',
        label: 'Dashboard de Equipo',
        icon: 'ChartBarSquareIcon',
        href: '/dashboard/team-performance',
        requiredPermission: 'team.view',
        group: 'performance',
      },
      {
        id: 'goals',
        label: 'Metas',
        icon: 'TrophyIcon',
        href: '/goals',
        requiredPermission: 'team.view',
        group: 'performance',
      },
      {
        id: 'leaderboard',
        label: 'Leaderboard',
        icon: 'TrophyIcon',
        href: '/leaderboard',
        requiredPermission: 'team.view',
        group: 'performance',
      },
      // Comisiones — configuracion y gestion unificada
      {
        id: 'commission-plans',
        label: 'Planes de Comision',
        icon: 'ClipboardDocumentListIcon',
        href: '/commission-plans',
        requiredPermission: 'team.view',
        group: 'commissions',
      },
      {
        id: 'advisor-commissions',
        label: 'Comisiones Asesores',
        icon: 'BanknotesIcon',
        href: '/advisor-commissions',
        requiredPermission: 'team.view',
        group: 'commissions',
      },
      {
        id: 'broker-commissions-unified',
        label: 'Comisiones Brokers',
        icon: 'BanknotesIcon',
        href: '/broker-commissions',
        requiredPermission: 'team.view',
        group: 'commissions',
      },
      {
        id: 'leader-overrides',
        label: 'Coordinadores',
        icon: 'UserGroupIcon',
        href: '/leader-overrides',
        requiredPermission: 'team.view',
        group: 'commissions',
      },
      {
        id: 'commission-analytics',
        label: 'Analytics',
        icon: 'ChartBarIcon',
        href: '/commission-analytics',
        requiredPermission: 'team.view',
        group: 'commissions',
      },
      {
        id: 'departments',
        label: 'Departamentos',
        icon: 'BuildingOffice2Icon',
        href: '/departments',
        requiredPermission: 'team.view',
        group: 'org',
      },
    ],
  },

  // ========== BROKERS ==========
  // Dedicated module for external broker/agent management
  // Note: learning/training/certification content has moved to the "Learning" module.
  {
    id: 'brokers',
    label: 'Brokers',
    labelEn: 'Brokers',
    icon: 'IdentificationIcon',
    scope: 'shared',
    requiredPermission: 'team.view',
    groups: [
      { id: 'pipeline', label: 'Pipeline', labelEn: 'Pipeline', icon: 'FunnelIcon' },
      { id: 'management', label: 'Gestión', labelEn: 'Management', icon: 'UsersIcon' },
      { id: 'operations', label: 'Operaciones', labelEn: 'Operations', icon: 'CogIcon' },
    ],
    items: [
      // Pipeline — vista estratégica
      {
        id: 'broker-funnel',
        label: 'Funnel de Brokers',
        icon: 'FunnelIcon',
        href: '/broker-funnel',
        requiredPermission: 'team.view',
        group: 'pipeline',
      },
      // Gestión — administración de brokers
      {
        id: 'broker-applications',
        label: 'Aplicaciones',
        icon: 'DocumentCheckIcon',
        href: '/broker-applications',
        requiredPermission: 'team.view',
        group: 'management',
      },
      {
        id: 'brokers',
        label: 'Brokers',
        icon: 'UsersIcon',
        href: '/brokers',
        matchPaths: ['/brokers/*'],
        requiredPermission: 'team.view',
        group: 'management',
      },
      {
        id: 'broker-organizations',
        label: 'Organizaciones',
        icon: 'BuildingOffice2Icon',
        href: '/broker-organizations',
        requiredPermission: 'team.view',
        group: 'management',
      },
      // Operaciones — día a día
      {
        id: 'broker-conflicts',
        label: 'Conflictos',
        icon: 'ShieldCheckIcon',
        href: '/broker-conflicts',
        requiredPermission: 'team.view',
        group: 'operations',
      },
      {
        id: 'broker-sources',
        label: 'Fuentes de Brokers',
        icon: 'SignalIcon',
        href: '/broker-sources',
        requiredPermission: 'team.view',
        group: 'operations',
      },
    ],
  },

  // ========== LEARNING ==========
  // Capacitación, exámenes, materiales, diplomas y certificaciones.
  // Extracted from the Brokers module so it's a top-level domain on its own.
  {
    id: 'learning',
    label: 'Learning',
    labelEn: 'Learning',
    icon: 'AcademicCapIcon',
    scope: 'shared',
    requiredPermission: 'learning.view',
    groups: [
      { id: 'my', label: 'Mi Capacitación', labelEn: 'My Training', icon: 'UserIcon' },
      { id: 'content', label: 'Contenido', labelEn: 'Content', icon: 'BookOpenIcon' },
      { id: 'evaluation', label: 'Evaluación', labelEn: 'Evaluation', icon: 'ClipboardDocumentCheckIcon' },
      { id: 'certification', label: 'Certificación', labelEn: 'Certification', icon: 'TrophyIcon' },
    ],
    items: [
      // ── MI CAPACITACIÓN (broker-personal: curso + examen) ────────────────
      {
        id: 'broker-learning',
        label: 'Mi Capacitación',
        icon: 'AcademicCapIcon',
        href: '/dashboard/broker/learning',
        matchPaths: ['/dashboard/broker/learning/*'],
        badge: 'Nuevo',
        badgeVariant: 'info',
        requiredPermission: 'learning.view',
        group: 'my',
      },
      // 'broker-test' removed from sidebar — the exam is step 7 INSIDE the
      // training path. Exposing a separate "Examen" shortcut created
      // duplicated entry points and confused brokers. The route
      // /dashboard/broker/certification/test still exists for deep-links.
      // ── ADMIN (platform/tenant training management) ──────────────────────
      {
        id: 'learning-paths',
        label: 'Rutas de Capacitación',
        icon: 'MapIcon',
        href: '/learning/paths',
        // matchPaths include child-of-path surfaces (exams, forms when
        // reached from within a path config) so the sidebar highlight stays
        // consistent — admin editing an exam that belongs to a path still
        // sees "Rutas de Capacitación" as active.
        matchPaths: [
          '/learning/paths/*',
          '/learning/exams',
          '/learning/exams/*',
        ],
        requiredPermission: 'learning.view',
        group: 'content',
      },
      {
        id: 'learning-materials',
        label: 'Materiales',
        icon: 'DocumentTextIcon',
        href: '/learning/materials',
        requiredPermission: 'learning.view',
        group: 'content',
      },
      // 'learning-exams' removed from sidebar — exams belong to a training
      // path's step 7. Admins edit them from inside the path's step editor
      // ("+ Nuevo ↗" / "Editar preguntas ↗" buttons). The /learning/exams
      // route itself stays functional for deep-link / library view.
      {
        id: 'learning-forms',
        label: 'Formularios',
        icon: 'Bars3BottomLeftIcon',
        href: '/learning/forms',
        requiredPermission: 'learning.view',
        group: 'content',
      },
      {
        id: 'learning-diploma-templates',
        label: 'Plantillas de Diploma',
        icon: 'TrophyIcon',
        href: '/learning/diploma-templates',
        requiredPermission: 'learning.view',
        group: 'certification',
      },
      {
        id: 'learning-certifications',
        label: 'Certificaciones',
        icon: 'ShieldCheckIcon',
        href: '/learning/certifications',
        matchPaths: ['/learning/certifications*'],
        requiredPermission: 'learning.view',
        group: 'certification',
      },
    ],
  },

  // ========== DOCUMENTOS ==========
  // Artefactos y paperwork personal del broker/agente: identidad/compliance,
  // acuerdos firmados, y certificaciones emitidas (diploma). A diferencia
  // de Learning (proceso), aquí viven los documentos finales que el usuario
  // consulta, descarga o actualiza a lo largo del tiempo.
  {
    id: 'documentos',
    label: 'Documentos',
    labelEn: 'Documents',
    icon: 'DocumentTextIcon',
    scope: 'shared',
    requiredPermission: 'documentos.view',
    groups: [
      { id: 'personal', label: 'Mis Documentos', labelEn: 'My Documents', icon: 'UserIcon' },
    ],
    items: [
      {
        id: 'broker-compliance',
        label: 'Identidad y Compliance',
        icon: 'ShieldCheckIcon',
        href: '/dashboard/broker/certification/compliance',
        requiredPermission: 'documentos.view',
        group: 'personal',
      },
      {
        id: 'broker-agreement',
        label: 'Acuerdos',
        icon: 'DocumentTextIcon',
        href: '/dashboard/broker/certification/agreement',
        requiredPermission: 'documentos.view',
        group: 'personal',
      },
      {
        id: 'broker-certifications',
        label: 'Mis Certificaciones',
        icon: 'TrophyIcon',
        href: '/dashboard/broker/certifications',
        matchPaths: ['/dashboard/broker/certifications*', '/dashboard/broker/certification/diploma'],
        requiredPermission: 'documentos.view',
        group: 'personal',
      },
    ],
  },

  // ========== COMUNICACIONES ==========
  // Omnicanal (external) + Chat Interno (team) under one module
  {
    id: 'communications',
    label: 'Comunicaciones',
    labelEn: 'Communications',
    icon: 'ChatBubbleLeftIcon',
    scope: 'shared',
    requiredPermission: 'communications.view',
    groups: [
      { id: 'channels', label: 'Canales', labelEn: 'Channels', icon: 'ChatBubbleLeftIcon' },
      { id: 'team', label: 'Equipo', labelEn: 'Team', icon: 'ChatBubbleLeftRightIcon' },
      { id: 'ai', label: 'IA', labelEn: 'AI', icon: 'SparklesIcon' },
    ],
    items: [
      {
        id: 'omnichannel',
        label: 'Omnicanal',
        icon: 'ChatBubbleLeftIcon',
        href: '/omnichannel',
        requiredPermission: 'communications.view',
        group: 'channels',
      },
      {
        id: 'team-chat',
        label: 'Chat Interno',
        icon: 'ChatBubbleLeftRightIcon',
        href: '/team-chat',
        requiredPermission: 'communications.view',
        group: 'team',
      },
      {
        id: 'dashboard',
        label: 'Centro de Comunicaciones',
        icon: 'ChartBarSquareIcon',
        href: '/dashboard/omnichannel',
        requiredPermission: 'communications.view',
        group: 'channels',
      },
      {
        id: 'ai-conversations',
        label: 'AI Conversations',
        icon: 'SparklesIcon',
        href: '/ai-conversations',
        requiredPermission: 'communications.view',
        group: 'ai',
      },
    ],
  },

  // ========== DISENO ==========
  // CONSISTENT: All items use 'diseno.view' as base permission
  {
    id: 'diseno',
    label: 'Diseño',
    labelEn: 'Design',
    icon: 'PaintBrushIcon',
    scope: 'shared',
    requiredPermission: 'diseno.view',
    groups: [
      { id: 'config', label: 'Configuración', labelEn: 'Configuration', icon: 'SwatchIcon' },
    ],
    items: [
      {
        id: 'design-system',
        label: 'Sistema de Diseño',
        icon: 'SwatchIcon',
        href: '/diseno',
        requiredPermission: 'diseno.view',
        group: 'config',
      },
      {
        id: 'design-landing',
        label: 'Landing Page',
        icon: 'GlobeAltIcon',
        href: '/diseno/landing',
        requiredPermission: 'diseno.view',
        group: 'config',
        badge: 'Pronto',
        badgeVariant: 'warning',
      },
    ],
  },

  // ========== MARKETING ==========
  {
    id: 'marketing',
    label: 'Marketing',
    labelEn: 'Marketing',
    icon: 'MegaphoneIcon',
    scope: 'shared',
    requiredPermission: 'marketing.view',
    groups: [
      { id: 'content-hub', label: 'Content Hub', labelEn: 'Content Hub', icon: 'DocumentTextIcon' },
      { id: 'email', label: 'Email', labelEn: 'Email', icon: 'EnvelopeIcon' },
      { id: 'whatsapp', label: 'WhatsApp', labelEn: 'WhatsApp', icon: 'ChatBubbleLeftRightIcon' },
    ],
    items: [
      {
        id: 'content-blog',
        label: 'Blog',
        icon: 'DocumentTextIcon',
        href: '/marketing/content/blog',
        badge: 'NEW',
        badgeVariant: 'success',
        requiredPermission: 'marketing.view',
        group: 'content-hub',
      },
      {
        id: 'content-pieces',
        label: 'Piezas',
        icon: 'PhotoIcon',
        href: '/marketing/content/pieces',
        requiredPermission: 'marketing.view',
        group: 'content-hub',
      },
      {
        id: 'content-calendar',
        label: 'Calendario',
        icon: 'CalendarIcon',
        href: '/marketing/content/calendar',
        requiredPermission: 'marketing.view',
        group: 'content-hub',
      },
      {
        id: 'email-builder',
        label: 'Email Builder',
        icon: 'EnvelopeIcon',
        href: '/marketing/email-builder',
        requiredPermission: 'marketing.view',
        group: 'email',
      },
      {
        id: 'whatsapp-templates',
        label: 'WhatsApp',
        icon: 'ChatBubbleLeftRightIcon',
        href: '/marketing/whatsapp-templates',
        requiredPermission: 'marketing.view',
        group: 'whatsapp',
      },
      {
        id: 'quick-replies',
        label: 'Respuestas Rapidas',
        icon: 'BoltIcon',
        href: '/marketing/quick-replies',
        requiredPermission: 'marketing.view',
        group: 'whatsapp',
      },
    ],
  },

  // ========== EVENTOS / ACTIVIDADES ==========
  // CONSISTENT: All items use 'activities.view' as base permission
  // Submodule visibility controlled by 'activities.{itemId}'
  {
    id: 'activities',
    label: 'Eventos',
    labelEn: 'Events',
    icon: 'CalendarIcon',
    scope: 'shared',
    requiredPermission: 'activities.view',
    groups: [
      { id: 'calendar', label: 'Calendario', labelEn: 'Calendar', icon: 'CalendarIcon' },
      { id: 'tasks', label: 'Tareas', labelEn: 'Tasks', icon: 'CheckCircleIcon' },
    ],
    items: [
      {
        id: 'calendar',
        label: 'Mi Calendario',
        icon: 'CalendarIcon',
        href: '/calendar',
        requiredPermission: 'activities.view',
        group: 'calendar',
      },
      {
        id: 'appointments',
        label: 'Citas Pendientes',
        icon: 'ClockIcon',
        href: '/pending-appointments',
        requiredPermission: 'activities.view',
        group: 'calendar',
      },
      {
        id: 'tasks',
        label: 'Tareas',
        icon: 'CheckCircleIcon',
        href: '/tasks',
        requiredPermission: 'activities.view',
        group: 'tasks',
      },
    ],
  },

  // ========== AI AGENTS ==========
  {
    id: 'ai-agents',
    label: 'AI Agents',
    labelEn: 'AI Agents',
    icon: 'SparklesIcon',
    scope: 'shared',
    requiredPermission: 'analytics.view',
    groups: [
      { id: 'agents', label: 'Agentes', labelEn: 'Agents', icon: 'CubeIcon' },
      { id: 'knowledge', label: 'Conocimiento', labelEn: 'Knowledge', icon: 'BookOpenIcon' },
      { id: 'monitoring', label: 'Monitoreo', labelEn: 'Monitoring', icon: 'ChartBarIcon' },
    ],
    items: [
      {
        id: 'agents-dashboard',
        label: 'Dashboard',
        icon: 'ChartBarSquareIcon',
        href: '/ai/dashboard',
        requiredPermission: 'analytics.view',
        group: 'agents',
      },
      {
        id: 'agents-list',
        label: 'Agentes',
        icon: 'CubeIcon',
        href: '/ai/agents',
        requiredPermission: 'analytics.view',
        group: 'agents',
        matchPaths: ['/ai/agents/*'],
      },
      {
        id: 'skills',
        label: 'Skills',
        icon: 'BookOpenIcon',
        href: '/ai/skills',
        requiredPermission: 'analytics.view',
        group: 'knowledge',
      },
      {
        id: 'skill-templates',
        label: 'Templates',
        icon: 'DocumentTextIcon',
        href: '/ai/skills/templates',
        requiredPermission: 'analytics.view',
        group: 'knowledge',
      },
      {
        id: 'executions',
        label: 'Ejecuciones',
        icon: 'ArrowPathIcon',
        href: '/ai/executions',
        requiredPermission: 'analytics.view',
        group: 'monitoring',
      },
      {
        id: 'ai-costs',
        label: 'Costos',
        icon: 'BanknotesIcon',
        href: '/ai/costs',
        requiredPermission: 'analytics.view',
        group: 'monitoring',
      },
    ],
  },

  // ========== INTEGRACIONES ==========
  // Gate canonico del modulo es `integrations.view` (antes se usaba
  // incorrectamente `analytics.view` por un accidente historico). La
  // data migration apps/users/migrations/0035 asegura retro-compat:
  // todo user que hoy tenga analytics.view recibe integrations.view.
  {
    id: 'integrations-module',
    label: 'Integraciones',
    labelEn: 'Integrations',
    icon: 'LinkIcon',
    scope: 'shared',
    requiredPermission: 'integrations.view',
    groups: [],
    items: [
      {
        id: 'integrations',
        label: 'Integraciones',
        icon: 'LinkIcon',
        href: '/integrations',
        badge: 'NEW',
        badgeVariant: 'primary',
        requiredPermission: 'integrations.view',
      },
      {
        id: 'crm-migrations',
        label: 'Migraciones CRM',
        icon: 'ArrowPathIcon',
        href: '/migrations',
        badge: 'NEW',
        badgeVariant: 'primary',
        requiredPermission: 'crm_migrations.view',
      },
    ],
  },

  // ========== ADMINISTRACIÓN ==========
  // CONSISTENT: All items use 'admin.view' as base permission
  // Submodule visibility controlled by 'admin.{itemId}'
  {
    id: 'admin',
    label: 'Administración',
    labelEn: 'Administration',
    icon: 'ShieldCheckIcon',
    scope: 'shared',
    requiredPermission: 'admin.view',
    groups: [
      { id: 'infrastructure', label: 'Infraestructura', labelEn: 'Infrastructure', icon: 'ServerIcon' },
      { id: 'finance', label: 'Finanzas', labelEn: 'Finance', icon: 'BanknotesIcon' },
    ],
    items: [
      {
        id: 'aws',
        label: 'AWS Management',
        icon: 'Cog6ToothIcon',
        href: '/aws-management',
        requiredPermission: 'admin.view',
        group: 'infrastructure',
      },
      {
        id: 'payroll',
        label: 'Nómina',
        icon: 'BanknotesIcon',
        href: '/payroll',
        requiredPermission: 'admin.view',
        group: 'finance',
      },
    ],
  },

  // ========== AUTOMATIZACIONES ==========
  // CONSISTENT: All items use 'automations.view' as base permission
  {
    id: 'automations',
    label: 'Automatizaciones',
    labelEn: 'Automations',
    icon: 'BoltIcon',
    scope: 'shared',
    requiredPermission: 'automations.view',
    groups: [
      { id: 'config', label: 'Configuración', labelEn: 'Configuration', icon: 'Cog6ToothIcon' },
    ],
    items: [
      {
        id: 'list',
        label: 'Lista de Automatizaciones',
        icon: 'BoltIcon',
        href: '/automations',
        requiredPermission: 'automations.view',
        group: 'config',
      },
      {
        id: 'active',
        label: 'Automatización Activa',
        icon: 'PlayIcon',
        href: '/automations/active',
        requiredPermission: 'automations.view',
        matchPaths: ['/automations/*'],
        group: 'config',
      },
    ],
  },

  // ========== FINANCIERO ==========
  // CONSISTENT: All items use 'financial.view' as base permission
  {
    id: 'financial',
    label: 'Financiero',
    labelEn: 'Financial',
    icon: 'BanknotesIcon',
    scope: 'shared',
    requiredPermission: 'financial.view',
    groups: [
      { id: 'overview', label: 'General', labelEn: 'Overview', icon: 'ChartBarIcon' },
      { id: 'transactions', label: 'Transacciones', labelEn: 'Transactions', icon: 'BanknotesIcon' },
    ],
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard Financiero',
        icon: 'ChartBarSquareIcon',
        href: '/financial',
        requiredPermission: 'financial.view',
        group: 'overview',
      },
      {
        id: 'incomes',
        label: 'Ingresos',
        icon: 'ArrowTrendingUpIcon',
        href: '/financial/incomes',
        requiredPermission: 'financial.view',
        group: 'transactions',
      },
      {
        id: 'costs',
        label: 'Costos',
        icon: 'ArrowTrendingDownIcon',
        href: '/financial/costs',
        requiredPermission: 'financial.view',
        group: 'transactions',
      },
      {
        id: 'budgets',
        label: 'Presupuestos',
        icon: 'CalculatorIcon',
        href: '/financial/budgets',
        requiredPermission: 'financial.view',
        group: 'transactions',
      },
    ],
  },

  // ========== SOPORTE (PLATFORM ONLY) ==========
  // CONSISTENT: All items use 'support.view' as base permission
  {
    id: 'support',
    label: 'Soporte',
    labelEn: 'Support',
    icon: 'LifebuoyIcon',
    scope: 'platform',
    platformTag: 'PLATFORM',
    requiredPermission: 'support.view',
    groups: [
      { id: 'tickets', label: 'Tickets', labelEn: 'Tickets', icon: 'TicketIcon' },
    ],
    items: [
      {
        id: 'tickets',
        label: 'Todos los Tickets',
        icon: 'TicketIcon',
        href: '/support/tickets',
        requiredPermission: 'support.view',
        platformTag: 'PLATFORM',
        group: 'tickets',
      },
      {
        id: 'open',
        label: 'Tickets Abiertos',
        icon: 'ExclamationCircleIcon',
        href: '/support/open',
        requiredPermission: 'support.view',
        platformTag: 'PLATFORM',
        group: 'tickets',
      },
      {
        id: 'sla',
        label: 'SLA Tracking',
        icon: 'ClockIcon',
        href: '/support/sla',
        requiredPermission: 'support.view',
        platformTag: 'PLATFORM',
        group: 'tickets',
      },
    ],
  },

  // ========== SISTEMA (PLATFORM ONLY) ==========
  // CONSISTENT: All items use 'system.view' as base permission
  {
    id: 'system',
    label: 'Sistema',
    labelEn: 'System',
    icon: 'ServerIcon',
    scope: 'platform',
    platformTag: 'PLATFORM',
    requiredPermission: 'system.view',
    groups: [
      { id: 'config', label: 'Configuración', labelEn: 'Configuration', icon: 'Cog6ToothIcon' },
    ],
    items: [
      {
        id: 'settings',
        label: 'Configuración General',
        icon: 'Cog6ToothIcon',
        href: '/system',
        requiredPermission: 'system.view',
        platformTag: 'PLATFORM',
        group: 'config',
      },
      {
        id: 'integrations',
        label: 'Integraciones Sistema',
        icon: 'BoltIcon',
        href: '/system/integrations',
        requiredPermission: 'system.view',
        platformTag: 'PLATFORM',
        group: 'config',
      },
      {
        id: 'api-keys',
        label: 'API Keys',
        icon: 'KeyIcon',
        href: '/system/api-keys',
        requiredPermission: 'system.view',
        platformTag: 'PLATFORM',
        group: 'config',
      },
    ],
  },

  // ========== MONITOREO ==========
  // CONSISTENT: All items use 'monitoring.view' as base permission
  // Shared items (docs, testing) + Platform items (uptime, performance, errors)
  {
    id: 'monitoring',
    label: 'Dev & Docs',
    labelEn: 'Dev & Docs',
    icon: 'EyeIcon',
    scope: 'shared',
    requiredPermission: 'monitoring.view',
    groups: [
      { id: 'docs', label: 'Documentación', labelEn: 'Documentation', icon: 'BookOpenIcon' },
      { id: 'testing', label: 'Testing', labelEn: 'Testing', icon: 'BeakerIcon' },
      { id: 'infra', label: 'Infraestructura', labelEn: 'Infrastructure', icon: 'ServerIcon' },
    ],
    items: [
      {
        id: 'docs',
        label: 'Documentación',
        icon: 'BookOpenIcon',
        href: '/docs',
        requiredPermission: 'monitoring.view',
        group: 'docs',
      },
      {
        id: 'testing',
        label: 'Testing & QA',
        icon: 'BeakerIcon',
        href: '/test',
        requiredPermission: 'monitoring.view',
        group: 'testing',
      },
      {
        id: 'tools',
        label: 'Developer Tools',
        icon: 'WrenchScrewdriverIcon',
        href: '/dev-tools',
        requiredPermission: 'monitoring.view',
        group: 'testing',
      },
      {
        id: 'uptime',
        label: 'Uptime',
        icon: 'SignalIcon',
        href: '/monitoring',
        requiredPermission: 'monitoring.view',
        scope: 'platform',
        platformTag: 'PLATFORM',
        group: 'infra',
      },
      {
        id: 'performance',
        label: 'Performance Sistema',
        icon: 'ChartBarIcon',
        href: '/monitoring/performance',
        requiredPermission: 'monitoring.view',
        scope: 'platform',
        platformTag: 'PLATFORM',
        group: 'infra',
      },
      {
        id: 'errors',
        label: 'Errores',
        icon: 'ExclamationTriangleIcon',
        href: '/monitoring/errors',
        requiredPermission: 'monitoring.view',
        scope: 'platform',
        platformTag: 'PLATFORM',
        group: 'infra',
      },
    ],
  },

  // ========== DESARROLLO (PLATFORM ONLY) ==========
  // CONSISTENT: All items use 'development.view' as base permission
  {
    id: 'development',
    label: 'Desarrollo',
    labelEn: 'Development',
    icon: 'CodeBracketIcon',
    scope: 'platform',
    platformTag: 'PLATFORM',
    requiredPermission: 'development.view',
    groups: [
      { id: 'planning', label: 'Planificación', labelEn: 'Planning', icon: 'MapIcon' },
    ],
    items: [
      {
        id: 'roadmap',
        label: 'Roadmap',
        icon: 'MapIcon',
        href: '/development',
        requiredPermission: 'development.view',
        platformTag: 'PLATFORM',
        group: 'planning',
      },
      {
        id: 'releases',
        label: 'Releases',
        icon: 'RocketLaunchIcon',
        href: '/development/releases',
        requiredPermission: 'development.view',
        platformTag: 'PLATFORM',
        group: 'planning',
      },
      {
        id: 'flags',
        label: 'Feature Flags',
        icon: 'FlagIcon',
        href: '/development/flags',
        requiredPermission: 'development.view',
        platformTag: 'PLATFORM',
        group: 'planning',
      },
    ],
  },

];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all unique module IDs (for backend sync)
 */
export function getAllModuleIds(): string[] {
  const moduleIds = new Set<string>();

  MODULES_CONFIG.forEach(module => {
    moduleIds.add(module.id);
    module.items.forEach(item => {
      if (item.requiredPermission) {
        const [moduleId] = item.requiredPermission.split('.');
        moduleIds.add(moduleId);
      }
    });
  });

  return Array.from(moduleIds).sort();
}

/**
 * Get module by ID
 */
export function getModuleById(id: string): ModuleConfig | undefined {
  return MODULES_CONFIG.find(m => m.id === id);
}

/**
 * Get all permissions for a module
 */
export function getModulePermissions(moduleId: string): string[] {
  return PERMISSION_ACTIONS.map(action => `${moduleId}.${action.code}`);
}

/**
 * Check if user has permission for a specific action on a module
 */
export function hasPermission(
  userPermissions: Record<string, boolean>,
  moduleId: string,
  action: PermissionAction
): boolean {
  const permissionKey = `${moduleId}.${action}`;
  return userPermissions[permissionKey] === true;
}

/**
 * Get all unique modules used across all items (for permission matrix)
 */
export function getPermissionModules(): { id: string; label: string }[] {
  const modules = new Map<string, string>();

  MODULES_CONFIG.forEach(section => {
    // Add section-level module
    modules.set(section.id, section.label);

    // Add item-level modules
    section.items.forEach(item => {
      if (item.requiredPermission) {
        const [moduleId] = item.requiredPermission.split('.');
        if (!modules.has(moduleId)) {
          // Try to find a better label from the section
          const label = moduleId.charAt(0).toUpperCase() + moduleId.slice(1);
          modules.set(moduleId, label);
        }
      }
    });
  });

  return Array.from(modules.entries()).map(([id, label]) => ({ id, label }));
}

/**
 * Get items grouped by their group ID for a specific module
 * Returns a map of groupId -> items[]
 */
export function getItemsByGroup(moduleId: string): Map<string, ModuleItem[]> {
  const module = getModuleById(moduleId);
  if (!module) return new Map();

  const grouped = new Map<string, ModuleItem[]>();

  // Initialize with defined groups (preserves order)
  module.groups?.forEach(group => {
    grouped.set(group.id, []);
  });

  // Add ungrouped category for items without group
  grouped.set('_ungrouped', []);

  // Assign items to groups
  module.items.forEach(item => {
    const groupId = item.group || '_ungrouped';
    const items = grouped.get(groupId) || [];
    items.push(item);
    grouped.set(groupId, items);
  });

  // Remove empty ungrouped
  if (grouped.get('_ungrouped')?.length === 0) {
    grouped.delete('_ungrouped');
  }

  return grouped;
}

/**
 * Get group info by ID for a specific module
 */
export function getGroupInfo(moduleId: string, groupId: string): ItemGroup | undefined {
  const module = getModuleById(moduleId);
  return module?.groups?.find(g => g.id === groupId);
}

/**
 * Get all modules with their items organized by groups
 * Useful for rendering hierarchical permission UI
 */
export function getModulesWithGroupedItems(): Array<{
  module: ModuleConfig;
  groupedItems: Array<{
    group: ItemGroup | null;
    items: ModuleItem[];
  }>;
}> {
  return MODULES_CONFIG.map(module => {
    const itemsByGroup = getItemsByGroup(module.id);
    const groupedItems: Array<{ group: ItemGroup | null; items: ModuleItem[] }> = [];

    itemsByGroup.forEach((items, groupId) => {
      if (groupId === '_ungrouped') {
        groupedItems.push({ group: null, items });
      } else {
        const groupInfo = module.groups?.find(g => g.id === groupId) || null;
        groupedItems.push({ group: groupInfo, items });
      }
    });

    return { module, groupedItems };
  });
}

// ============================================================================
// SCOPE FILTERING FUNCTIONS
// ============================================================================

/**
 * Get modules filtered by scope
 * @param isPlatformUser - True if user is OWLY platform member
 * @returns Filtered modules based on user type
 */
export function getModulesForUser(isPlatformUser: boolean): ModuleConfig[] {
  return MODULES_CONFIG.filter(module => {
    if (module.scope === 'shared') return true;
    if (module.scope === 'platform') return isPlatformUser;
    if (module.scope === 'tenant') return !isPlatformUser;
    return true;
  });
}

/**
 * Get module items filtered by scope
 * @param moduleId - Module ID to get items from
 * @param isPlatformUser - True if user is OWLY platform member
 * @returns Filtered items based on user type
 */
export function getModuleItemsForUser(moduleId: string, isPlatformUser: boolean): ModuleItem[] {
  const module = getModuleById(moduleId);
  if (!module) return [];

  return module.items.filter(item => {
    // Item scope overrides module scope
    const itemScope = item.scope || module.scope;

    if (itemScope === 'shared') return true;
    if (itemScope === 'platform') return isPlatformUser;
    if (itemScope === 'tenant') return !isPlatformUser;
    return true;
  });
}

/**
 * Check if an item should show the PLATFORM tag
 * @param item - The module item to check
 * @returns True if should show PLATFORM tag
 */
export function shouldShowPlatformTag(item: ModuleItem): boolean {
  return item.platformTag === 'PLATFORM';
}

/**
 * Get all platform-only modules
 */
export function getPlatformOnlyModules(): ModuleConfig[] {
  return MODULES_CONFIG.filter(module => module.scope === 'platform');
}

/**
 * Get all shared modules (visible to both Platform and Tenant)
 */
export function getSharedModules(): ModuleConfig[] {
  return MODULES_CONFIG.filter(module => module.scope === 'shared');
}

// ============================================================================
// BACKEND MODULES REFERENCE
// ============================================================================
// These IDs should match RolePermission.MODULES in backend/apps/users/models.py
//
// SHARED MODULES (Platform + Tenant):
// - dashboard: Principal (dashboards por rol, análisis, herramientas)
// - gestion: Gestión (mercados, compañías, proyectos, inventario, contenido)
// - leads: Leads (lista, personas, funnel, scoring, configuración)
// - clients: Clientes (lista, funnel, ciclo de vida, financiero)
// - communications: Comunicación (centro, omnicanal, inbox, AI)
// - activities: Eventos (calendario, citas, tareas)
// - analytics: AI & Automatización (AI center, insights, integraciones, config)
// - team: Equipo (usuarios, roles, permisos, rendimiento)
// - admin: Administración (AWS, nómina)
// - pipelines: Pipelines (lista, config, versiones)
// - financial: Financiero (dashboard, ingresos, costos, presupuestos)
// - monitoring: Dev & Docs (docs, testing, tools) + platform infra
//
// PLATFORM-ONLY MODULES (Solo OWLY):
// - support: Soporte (tickets, SLA tracking)
// - system: Sistema (config general, integraciones, API keys)
// - development: Desarrollo (roadmap, releases, feature flags)
// ============================================================================

export default MODULES_CONFIG;
